import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";

import logo from "../../assets/shared/dishonline-logo-white-sm.webp";
import {
   CONTACT_MAIL,
   EASE,
   EASE_IN_OUT,
   PillButton,
   RollText,
   WHATSAPP,
   scrollToTarget,
   smoothScroll,
} from "./landing/primitives";

const LINKS = [
   { id: "menu", label: "Het menu" },
   { id: "demo", label: "Live demo" },
   { id: "prijzen", label: "Prijzen" },
   { id: "faq", label: "FAQ" },
];

const NavAdmin = () => {
   const [menuOpen, setMenuOpen] = useState(false);
   const [hidden, setHidden] = useState(false);
   const [scrolled, setScrolled] = useState(false);
   const isLoggedIn = localStorage.getItem("token");
   const { pathname } = useLocation();
   const onHome = pathname === "/";

   const { scrollY } = useScroll();
   const last = useRef(0);
   useMotionValueEvent(scrollY, "change", (y) => {
      setScrolled(y > 24);
      setHidden(y > last.current && y > 240);
      last.current = y;
   });

   // Geen scrollende pagina achter het open menu
   useEffect(() => {
      if (!menuOpen) return;
      smoothScroll.lenis?.stop();
      document.body.style.overflow = "hidden";
      return () => {
         smoothScroll.lenis?.start();
         document.body.style.overflow = "";
      };
   }, [menuOpen]);

   const go = (e, id) => {
      setMenuOpen(false);
      if (!onHome) return; // gewone link naar /#id
      e.preventDefault();
      setTimeout(() => scrollToTarget(`#${id}`), menuOpen ? 450 : 0);
   };

   const cta = isLoggedIn ? (
      <NavLink to="/dashboard" onClick={() => setMenuOpen(false)}>
         <PillButton as="span" variant="ink">Dashboard</PillButton>
      </NavLink>
   ) : (
      <PillButton href={CONTACT_MAIL} variant="ink">
         Vraag een demo
      </PillButton>
   );

   return (
      <>
         <motion.header
            className="lp-root fixed inset-x-0 top-0 z-[150]"
            animate={{ y: hidden && !menuOpen ? "-110%" : "0%" }}
            transition={{ duration: 0.6, ease: EASE }}
         >
            {/* aankondiging, klapt weg zodra je scrolt */}
            <motion.div
               initial={false}
               animate={{ height: scrolled ? 0 : "auto", opacity: scrolled ? 0 : 1 }}
               transition={{ duration: 0.4, ease: EASE }}
               className="overflow-hidden bg-lp-ink text-lp-paper"
            >
               <p className="flex items-center justify-center gap-2 px-4 py-2 text-center text-[12px] sm:text-[13px]">
                  <span className="h-1.5 w-1.5 flex-shrink-0 animate-blink rounded-full bg-lp-blue" />
                  <span>
                     <span className="hidden sm:inline">Onze site is nog in opbouw. </span>Een demo?{" "}
                     <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white">
                        WhatsApp
                     </a>{" "}
                     of{" "}
                     <a href="mailto:info@resto-online.be" className="underline underline-offset-2 hover:text-white">
                        mail
                     </a>{" "}
                     ons.
                  </span>
               </p>
            </motion.div>

            <div className="px-3 pt-3 sm:px-5">
               <nav
                  className={`mx-auto flex max-w-[1400px] items-center justify-between rounded-full py-2 pl-5 pr-2 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
                     scrolled || menuOpen
                        ? "bg-lp-paper/75 shadow-[0_1px_0_rgba(18,17,16,0.06),0_20px_40px_-24px_rgba(18,17,16,0.25)] backdrop-blur-xl"
                        : "bg-transparent"
                  }`}
               >
                  <NavLink to="/" onClick={() => setMenuOpen(false)} className="relative z-[160] block" aria-label="Resto-Online home">
                     <img src={logo} alt="Resto-Online" width={480} height={191} className="h-8 w-auto sm:h-9" />
                  </NavLink>

                  <div className="hidden items-center gap-1 md:flex">
                     {LINKS.map((l) => (
                        <a
                           key={l.id}
                           href={`/#${l.id}`}
                           onClick={(e) => go(e, l.id)}
                           className="group rounded-full px-4 py-2 text-[14px] font-medium text-lp-ink/75 transition-colors hover:text-lp-ink"
                        >
                           <RollText>{l.label}</RollText>
                        </a>
                     ))}
                  </div>

                  <div className="flex items-center gap-2">
                     {!isLoggedIn && (
                        <NavLink
                           to="/inloggen"
                           className="group hidden rounded-full px-4 py-2 text-[14px] font-medium text-lp-ink/75 hover:text-lp-ink sm:block"
                        >
                           <RollText>Inloggen</RollText>
                        </NavLink>
                     )}
                     <div className="hidden sm:block">{cta}</div>
                     <button
                        onClick={() => setMenuOpen((o) => !o)}
                        aria-label={menuOpen ? "Menu sluiten" : "Menu openen"}
                        aria-expanded={menuOpen}
                        className="relative z-[160] flex h-11 w-11 flex-col items-center justify-center gap-[5px] rounded-full bg-lp-ink md:hidden"
                     >
                        <motion.span
                           animate={menuOpen ? { rotate: 45, y: 3.5 } : { rotate: 0, y: 0 }}
                           className="block h-[1.5px] w-4 bg-lp-paper"
                        />
                        <motion.span
                           animate={menuOpen ? { rotate: -45, y: -3 } : { rotate: 0, y: 0 }}
                           className="block h-[1.5px] w-4 bg-lp-paper"
                        />
                     </button>
                  </div>
               </nav>
            </div>
         </motion.header>

         {/* mobiel menu */}
         <AnimatePresence>
            {menuOpen && (
               <motion.div
                  className="lp-root lp-grain fixed inset-0 z-[140] flex flex-col bg-lp-paper px-6 pb-8 pt-32 md:hidden"
                  initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
                  animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
                  exit={{ clipPath: "inset(0% 0% 100% 0%)" }}
                  transition={{ duration: 0.7, ease: EASE_IN_OUT }}
               >
                  <ul className="flex flex-col">
                     {[...LINKS, ...(isLoggedIn ? [] : [{ id: "inloggen", label: "Inloggen", to: "/inloggen" }])].map((l, i) => (
                        <li key={l.id} className="overflow-hidden border-b border-lp-ink/10">
                           <motion.div
                              initial={{ y: "100%" }}
                              animate={{ y: 0 }}
                              transition={{ duration: 0.8, ease: EASE, delay: 0.25 + i * 0.06 }}
                           >
                              {l.to ? (
                                 <NavLink to={l.to} onClick={() => setMenuOpen(false)} className="flex items-baseline gap-4 py-4">
                                    <span className="font-ticket text-[11px] text-lp-muted">0{i + 1}</span>
                                    <span className="text-[2.6rem] font-semibold leading-none tracking-[-0.04em] text-lp-ink">{l.label}</span>
                                 </NavLink>
                              ) : (
                                 <a href={`/#${l.id}`} onClick={(e) => go(e, l.id)} className="flex items-baseline gap-4 py-4">
                                    <span className="font-ticket text-[11px] text-lp-muted">0{i + 1}</span>
                                    <span className="text-[2.6rem] font-semibold leading-none tracking-[-0.04em] text-lp-ink">{l.label}</span>
                                 </a>
                              )}
                           </motion.div>
                        </li>
                     ))}
                  </ul>
                  <motion.div
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.6, ease: EASE, delay: 0.6 }}
                     className="mt-auto flex flex-col items-start gap-4"
                  >
                     {cta}
                     <p className="font-ticket text-[11px] uppercase tracking-[0.16em] text-lp-muted">info@resto-online.be</p>
                  </motion.div>
               </motion.div>
            )}
         </AnimatePresence>
      </>
   );
};

export default NavAdmin;
